/**
 * Provides an administrative interface for configuring the Disbursement
 * Requests widget integration settings. Allows administrators to manage
 * Fluxx URL mappings used for disbursement request navigation, including
 * direct record access and "View in Fluxx" actions. Supports retrieval of
 * existing widget configurations, modification of integration endpoints,
 * change tracking, save operations, and restoration of previously saved
 * configuration values.
 */
import React, { useEffect, useState } from "react";
import {
  getWidgetConfigurations,
  updateWidgetConfigurations,
} from "@/services/widgetConfig";

const FEATURE_CODE = "dashboard.disbursementrequest";

const DisbursementConfig: React.FC = () => {
  const [savedConfig, setSavedConfig] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const [disbursementFluxxUrl1, setDisbursementFluxxUrl1] =
    useState("");
  const [disbursementFluxxUrl2, setDisbursementFluxxUrl2] =
    useState("");

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    const config = await getWidgetConfigurations();

    const disbursementConfig = config?.[FEATURE_CODE] || {};

    setSavedConfig(disbursementConfig);

    setDisbursementFluxxUrl1(
      disbursementConfig.DisbursementFluxx1 || "",
    );

    setDisbursementFluxxUrl2(
      disbursementConfig.DisbursementFluxx2 || "",
    );
  };

  const hasChanges =
    !!savedConfig &&
    (
      disbursementFluxxUrl1 !==
        (savedConfig.DisbursementFluxx1 || "") ||
      disbursementFluxxUrl2 !==
        (savedConfig.DisbursementFluxx2 || "")
    );

  const handleSave = async () => {
    setSaving(true);

    try {
      const payload = {
        featureCode: FEATURE_CODE,
        configs: {
          DisbursementFluxx1: disbursementFluxxUrl1,
          DisbursementFluxx2: disbursementFluxxUrl2,
        },
      };

      await updateWidgetConfigurations(payload);

      setSavedConfig(payload.configs);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!savedConfig) return;

    setDisbursementFluxxUrl1(
      savedConfig.DisbursementFluxx1 || "",
    );

    setDisbursementFluxxUrl2(
      savedConfig.DisbursementFluxx2 || "",
    );
  };

  return (
    <div className="border border-gray-200 rounded-md bg-[#fbfaf7] p-3 space-y-3">
      <div className="flex items-center gap-4 text-xs">
        <span className="w-44 text-gray-700">
          Fluxx URL (for each record)
        </span>

        <input
          type="text"
          value={disbursementFluxxUrl1}
          onChange={(e) =>
            setDisbursementFluxxUrl1(e.target.value)
          }
          className="flex-1 border border-gray-300 rounded px-3 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
          placeholder="Enter Fluxx URL"
        />
      </div>

      <div className="flex items-center gap-4 text-xs">
        <span className="w-44 text-gray-700">
          Fluxx URL (view in Fluxx)
        </span>

        <input
          type="text"
          value={disbursementFluxxUrl2}
          onChange={(e) =>
            setDisbursementFluxxUrl2(e.target.value)
          }
          className="flex-1 border border-gray-300 rounded px-3 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
          placeholder="Enter Fluxx URL"
        />
      </div>

      <div className="flex justify-end gap-2 items-center pt-2">
        {hasChanges && (
          <div className="text-orange-600 border border-orange-300 bg-orange-50 px-2 py-0.5 rounded text-xs">
            Unsaved
          </div>
        )}

        <button
          className="bg-[#e7e5e0] px-3 py-1 text-xs"
          onClick={handleCancel}
          disabled={!hasChanges}
        >
          Cancel
        </button>

        <button
          className="bg-[var(--color-brand)] text-white px-3 py-1 text-xs"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default DisbursementConfig;